import React, { useState, useContext, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus,
  faPlane,
  faBus,
  faTrain,
  faTimes,
  faUpload,
  faChevronDown, 
  faChevronUp, 
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { doc, updateDoc, getDoc, Timestamp, addDoc, collection, serverTimestamp, getDocs } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { TourContext } from '../../context/TourContext';
import { UserContext } from '../../context/UserContext';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import styles from './TicketModal.module.css'
import defaultProfilePhoto from '../../assets/Images/defaultUserProfilePicture.png';

const TicketModal = ({ editingTicket = null, isOpen, onClose }) => {
  const { selectedEvent, selectedDate, updateDate } = useContext(TourContext);
  const { userData, authToken } = useContext(UserContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMode, setSelectedMode] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedVisibility, setSelectedVisibility] = useState([]);
  const [selectedPassengers, setSelectedPassengers] = useState([]);
  const [isPassengerDropdownOpen, setIsPassengerDropdownOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [visibilityIds, setVisibilityIds] = useState([]);

  // Determine event type from selectedEvent
  const eventType = selectedEvent?.type || 'tour';

  const [ticketData, setTicketData] = useState({
    passengerName: '',
    origin: '',
    destination: '',
    departureTime: '',
    arrivalTime: '',
    flightNumber: '',
    trainNumber: '',
    busNumber: '',
    pnrNumber: '',
    notes: '',
    airlines: '',
    travelMode: '',
  });

  const resetForm = () => {
    setTicketData({
      passengerName: '',
      origin: '',
      destination: '',
      departureTime: '',
      arrivalTime: '',
      flightNumber: '',
      trainNumber: '',
      busNumber: '',
      pnrNumber: '',
      notes: '',
      airlines: '',
      travelMode: '',
    });
    setSelectedMode(null);
    onClose(); // Add this line to notify parent component
  };

  useEffect(() => {
    if (editingTicket) {
      setSelectedMode(editingTicket.travelMode);
      
      // Convert Firebase timestamps to Date objects for the date picker
      let departureTime = '';
      let arrivalTime = '';
      
      if (editingTicket.departureTime) {
        if (editingTicket.departureTime.seconds) {
          departureTime = new Date(editingTicket.departureTime.seconds * 1000).toISOString();
        } else if (editingTicket.departureTime instanceof Date) {
          departureTime = editingTicket.departureTime.toISOString();
        } else {
          departureTime = new Date(editingTicket.departureTime).toISOString();
        }
      }
      
      if (editingTicket.arrivalTime) {
        if (editingTicket.arrivalTime.seconds) {
          arrivalTime = new Date(editingTicket.arrivalTime.seconds * 1000).toISOString();
        } else if (editingTicket.arrivalTime instanceof Date) {
          arrivalTime = editingTicket.arrivalTime.toISOString();
        } else {
          arrivalTime = new Date(editingTicket.arrivalTime).toISOString();
        }
      }
      
      setTicketData({
        passengerName: editingTicket.passengerName || '',
        origin: editingTicket.origin || '',
        destination: editingTicket.destination || '',
        departureTime: departureTime,
        arrivalTime: arrivalTime,
        flightNumber: editingTicket.flightNumber || '',
        trainNumber: editingTicket.trainNumber || '',
        busNumber: editingTicket.busNumber || '',
        pnrNumber: editingTicket.pnrNumber || '',
        notes: editingTicket.notes || '',
        airlines: editingTicket.airlines || '',
        travelMode: editingTicket.travelMode || '',
      });
      
      setSelectedPassengers(editingTicket.passengers || []);
      
      // Store the visibility IDs for now - we'll handle them when we fetch members
      if (editingTicket.visibility && editingTicket.visibility.length > 0) {
        setVisibilityIds(editingTicket.visibility);
      }
    }
  }, [editingTicket]);

  const handleModeSelect = (mode) => {
    setSelectedMode(mode);
    setTicketData(prev => ({
      ...prev,
      travelMode: mode
    }));
  };

  useEffect(() => {
    setSelectedPassengers([]);
  }, [selectedDate]);

  useEffect(() => {
    setIsModalOpen(isOpen);
  }, [isOpen]);

  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Upload function - already matching the AttachmentDetails pattern
  const uploadFile = async (file) => {
    try {
      setUploadProgress(10);
      console.log('Starting file upload using processAndUploadMedia');
      
      // Create FormData
      const formData = new FormData();
      
      // Use the 'media' field name to match what processAndUploadMedia expects
      formData.append('media', file);
      
      // Include type information
      formData.append('type', 'attachment'); // Use 'attachment' type like other file uploads
      
      // Include tour and date info directly in the query params
      const queryParams = new URLSearchParams({
        context: 'ticket',  // Indicate this is a ticket
        eventId: selectedEvent.id, 
        dateId: selectedDate,
        eventType: eventType
      }).toString();
      
      console.log('Uploading with params:', queryParams);
      
      // Make the request to the API endpoint
      const response = await fetch(`/api/processAndUploadMedia?${queryParams}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`
        },
        body: formData
      });
      
      console.log('Response status:', response.status, response.statusText);
      
      // Check for error responses
      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          console.error('Server error response:', errorData);
          throw new Error(errorData.error || errorData.details || 'Failed to upload file');
        } else {
          // If response is not JSON, read as text and throw error
          const errorText = await response.text();
          console.error('Non-JSON error response:', errorText);
          throw new Error('Server returned an unexpected response format');
        }
      }
      
      const responseData = await response.json();
      console.log('Upload successful, response:', responseData);
      
      // Handle different response structures
      let filePath, url, fileName, contentType;
      
      if (responseData.files && responseData.files.length > 0) {
        // Get the first file if multiple were uploaded
        const fileInfo = responseData.files[0];
        filePath = fileInfo.filePath;
        url = fileInfo.url;
        fileName = fileInfo.fileName || fileInfo.originalName;
        contentType = fileInfo.contentType;
      } else if (responseData.filePath || responseData.url) {
        // Direct file info for backward compatibility
        filePath = responseData.filePath;
        url = responseData.url;
        fileName = responseData.fileName;
        contentType = responseData.contentType;
      } else {
        console.error('Unexpected response structure:', responseData);
        throw new Error('Upload succeeded but response format is unexpected');
      }
      
      if (!url && !filePath) {
        throw new Error('No URL or file path returned from upload');
      }
      
      setUploadProgress(100);
      
      return {
        filePath: filePath || '',
        url: url || '',
        fileName: fileName || file.name,
        contentType: contentType || file.type,
        size: file.size
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadProgress(0);
      throw error;
    }
  };

  // Add airlines data
  const airlines = [
    { name: 'Aegean Airlines', userId: 'Aegean Airlines' },
    { name: 'Aer Lingus', userId: 'Aer Lingus' },
    { name: 'Aeroflot', userId: 'Aeroflot' },
    { name: 'Aerolineas Argentinas', userId: 'Aerolineas Argentinas' },
    { name: 'Aeromexico', userId: 'Aeromexico' },
    { name: 'Air Arabia', userId: 'Air Arabia' },
    { name: 'Air Astana', userId: 'Air Astana' },
    { name: 'Air Austral', userId: 'Air Austral' },
    { name: 'Air Baltic', userId: 'Air Baltic' },
    { name: 'Air Belgium', userId: 'Air Belgium' },
    { name: 'Air Canada', userId: 'Air Canada' },
    { name: 'Air Caraibes', userId: 'Air Caraibes' },
    { name: 'Air China', userId: 'Air China' },
    { name: 'Air Corsica', userId: 'Air Corsica' },
    { name: 'Air Dolomiti', userId: 'Air Dolomiti' },
    { name: 'Air Europa', userId: 'Air Europa' },
    { name: 'Air France', userId: 'Air France' },
    { name: 'Air India', userId: 'Air India' },
    { name: 'Air India Express', userId: 'Air India Express' },
    { name: 'Air Macau', userId: 'Air Macau' },
    { name: 'Air Malta', userId: 'Air Malta' },
    { name: 'Air Mauritius', userId: 'Air Mauritius' },
    { name: 'Air Namibia', userId: 'Air Namibia' },
    { name: 'Air New Zealand', userId: 'Air New Zealand' },
    { name: 'Air North', userId: 'Air North' },
    { name: 'Air Seoul', userId: 'Air Seoul' },
    { name: 'Air Serbia', userId: 'Air Serbia' },
    { name: 'Air Tahiti Nui', userId: 'Air Tahiti Nui' },
    { name: 'Air Transat', userId: 'Air Transat' },
    { name: 'Air Vanuatu', userId: 'Air Vanuatu' },
    { name: 'AirAsia', userId: 'AirAsia' },
    { name: 'AirAsia X', userId: 'AirAsia X' },
    { name: 'Aircalin', userId: 'Aircalin' },
    { name: 'Akasa Air', userId: 'Akasa Air' },
    { name: 'Alaska Airlines', userId: 'Alaska Airlines' },
    { name: 'Alitalia', userId: 'Alitalia' },
    { name: 'Allegiant', userId: 'Allegiant' },
    { name: 'American Airlines', userId: 'American Airlines' },
    { name: 'ANA', userId: 'ANA' },
    { name: 'Asiana', userId: 'Asiana' },
    { name: 'Austrian', userId: 'Austrian' },
    { name: 'Avianca', userId: 'Avianca' },
    { name: 'Azerbaijan Hava Yollary', userId: 'Azerbaijan Hava Yollary' },
    { name: 'Azores Airlines', userId: 'Azores Airlines' },
    { name: 'Azul', userId: 'Azul' },
    { name: 'Bamboo Airways', userId: 'Bamboo Airways' },
    { name: 'Bangkok Airways', userId: 'Bangkok Airways' },
    { name: 'British Airways', userId: 'British Airways' },
    { name: 'Brussels Airlines', userId: 'Brussels Airlines' },
    { name: 'Caribbean Airlines', userId: 'Caribbean Airlines' },
    { name: 'Cathay Dragon', userId: 'Cathay Dragon' },
    { name: 'Cathay Pacific', userId: 'Cathay Pacific' },
    { name: 'Cayman Airways', userId: 'Cayman Airways' },
    { name: 'CEBU Pacific Air', userId: 'CEBU Pacific Air' },
    { name: 'China Airlines', userId: 'China Airlines' },
    { name: 'China Eastern', userId: 'China Eastern' },
    { name: 'China Southern', userId: 'China Southern' },
    { name: 'Condor', userId: 'Condor' },
    { name: 'Copa Airlines', userId: 'Copa Airlines' },
    { name: 'Croatia Airlines', userId: 'Croatia Airlines' },
    { name: 'Czech Airlines', userId: 'Czech Airlines' },
    { name: 'Delta', userId: 'Delta' },
    { name: 'easyJet', userId: 'easyJet' },
    { name: 'Edelweiss Air', userId: 'Edelweiss Air' },
    { name: 'Egyptair', userId: 'Egyptair' },
    { name: 'EL AL', userId: 'EL AL' },
    { name: 'Emirates', userId: 'Emirates' },
    { name: 'Ethiopian Airlines', userId: 'Ethiopian Airlines' },
    { name: 'Etihad', userId: 'Etihad' },
    { name: 'Eurowings', userId: 'Eurowings' },
    { name: 'EVA Air', userId: 'EVA Air' },
    { name: 'Fiji Airways', userId: 'Fiji Airways' },
    { name: 'Finnair', userId: 'Finnair' },
    { name: 'flydubai', userId: 'flydubai' },
    { name: 'FlyOne', userId: 'FlyOne' },
    { name: 'French bee', userId: 'French bee' },
    { name: 'Frontier', userId: 'Frontier' },
    { name: 'Garuda Indonesia', userId: 'Garuda Indonesia' },
    { name: 'Gol', userId: 'Gol' },
    { name: 'Gulf Air', userId: 'Gulf Air' },
    { name: 'Hainan Airlines', userId: 'Hainan Airlines' },
    { name: 'Hawaiian Airlines', userId: 'Hawaiian Airlines' },
    { name: 'Helvetic Airways', userId: 'Helvetic Airways' },
    { name: 'HK Express', userId: 'HK Express' },
    { name: 'Hong Kong Airlines', userId: 'Hong Kong Airlines' },
    { name: 'Iberia', userId: 'Iberia' },
    { name: 'Icelandair', userId: 'Icelandair' },
    { name: 'IndiGo', userId: 'IndiGo' },
    { name: 'InterJet', userId: 'InterJet' },
    { name: 'ITA Airways', userId: 'ITA Airways' },
    { name: 'Japan Airlines', userId: 'Japan Airlines' },
    { name: 'Jeju Air', userId: 'Jeju Air' },
    { name: 'Jet2', userId: 'Jet2' },
    { name: 'JetBlue', userId: 'JetBlue' },
    { name: 'Jetstar', userId: 'Jetstar' },
    { name: 'Jin Air', userId: 'Jin Air' },
    { name: 'Kenya Airways', userId: 'Kenya Airways' },
    { name: 'KLM', userId: 'KLM' },
    { name: 'Korean Air', userId: 'Korean Air' },
    { name: 'Kulula', userId: 'Kulula' },
    { name: 'La Compagnie', userId: 'La Compagnie' },
    { name: 'LATAM', userId: 'LATAM' },
    { name: 'Lion Airlines', userId: 'Lion Airlines' },
    { name: 'LOT Polish Airlines', userId: 'LOT Polish Airlines' },
    { name: 'Lufthansa', userId: 'Lufthansa' },
    { name: 'Luxair', userId: 'Luxair' },
    { name: 'Malaysia Airlines', userId: 'Malaysia Airlines' },
    { name: 'Mango', userId: 'Mango' },
    { name: 'Middle East Airlines', userId: 'Middle East Airlines' },
    { name: 'Nok Air', userId: 'Nok Air' },
    { name: 'Nordwind Airlines', userId: 'Nordwind Airlines' },
    { name: 'Norwegian Air International', userId: 'Norwegian Air International' },
    { name: 'Norwegian Air Shuttle', userId: 'Norwegian Air Shuttle' },
    { name: 'Norwegian Air Sweden', userId: 'Norwegian Air Sweden' },
    { name: 'Norwegian Air UK', userId: 'Norwegian Air UK' },
    { name: 'Oman Air', userId: 'Oman Air' },
    { name: 'Pakistan International Airlines', userId: 'Pakistan International Airlines' },
    { name: 'Peach', userId: 'Peach' },
    { name: 'Pegasus Airlines', userId: 'Pegasus Airlines' },
    { name: 'Philippine Airlines', userId: 'Philippine Airlines' },
    { name: 'Porter', userId: 'Porter' },
    { name: 'Qantas', userId: 'Qantas' },
    { name: 'Qatar Airways', userId: 'Qatar Airways' },
    { name: 'Regional Express', userId: 'Regional Express' },
    { name: 'Rossiya - Russian Airlines', userId: 'Rossiya - Russian Airlines' },
    { name: 'Royal Air Maroc', userId: 'Royal Air Maroc' },
    { name: 'Royal Brunei', userId: 'Royal Brunei' },
    { name: 'Royal Jordanian', userId: 'Royal Jordanian' },
    { name: 'RwandAir', userId: 'RwandAir' },
    { name: 'Ryanair', userId: 'Ryanair' },
    { name: 'S7 Airlines', userId: 'S7 Airlines' },
    { name: 'SAS', userId: 'SAS' },
    { name: 'Saudia', userId: 'Saudia' },
    { name: 'Scoot Airlines', userId: 'Scoot Airlines' },
    { name: 'Shanghai Airlines', userId: 'Shanghai Airlines' },
    { name: 'Silkair', userId: 'Silkair' },
    { name: 'Silver', userId: 'Silver' },
    { name: 'Singapore Airlines', userId: 'Singapore Airlines' },
    { name: 'Skylanes', userId: 'Skylanes' },
    { name: 'South African Airways', userId: 'South African Airways' },
    { name: 'Southwest', userId: 'Southwest' },
    { name: 'SpiceJet', userId: 'SpiceJet' },
    { name: 'Spirit', userId: 'Spirit' },
    { name: 'Spring Airlines', userId: 'Spring Airlines' },
    { name: 'Spring Japan', userId: 'Spring Japan' },
    { name: 'SriLankan Airlines', userId: 'SriLankan Airlines' },
    { name: 'Sun Country', userId: 'Sun Country' },
    { name: 'Sunclass Airlines', userId: 'Sunclass Airlines' },
    { name: 'Sunwing', userId: 'Sunwing' },
    { name: 'SWISS', userId: 'SWISS' },
    { name: 'Swoop', userId: 'Swoop' },
    { name: 'TAAG', userId: 'TAAG' },
    { name: 'TACA', userId: 'TACA' },
    { name: 'TAP Portugal', userId: 'TAP Portugal' },
    { name: 'THAI', userId: 'THAI' },
    { name: 'tigerair Australia', userId: 'tigerair Australia' },
    { name: 'Transavia Airlines', userId: 'Transavia Airlines' },
    { name: 'TUI UK', userId: 'TUI UK' },
    { name: 'TUIfly', userId: 'TUIfly' },
    { name: 'Tunis Air', userId: 'Tunis Air' },
    { name: 'Turkish Airlines', userId: 'Turkish Airlines' },
    { name: 'Ukraine International', userId: 'Ukraine International' },
    { name: 'United', userId: 'United' },
    { name: 'Ural Airlines', userId: 'Ural Airlines' },
    { name: 'UTair Aviation', userId: 'UTair Aviation' },
    { name: 'Uzbekistan Airways', userId: 'Uzbekistan Airways' },
    { name: 'Vietnam Airlines', userId: 'Vietnam Airlines' },
    { name: 'Virgin Atlantic', userId: 'Virgin Atlantic' },
    { name: 'Virgin Australia', userId: 'Virgin Australia' },
    { name: 'Vistara', userId: 'Vistara' },
    { name: 'Viva Aerobus', userId: 'Viva Aerobus' },
    { name: 'Volaris', userId: 'Volaris' },
    { name: 'Volotea', userId: 'Volotea' },
    { name: 'Vueling Airlines', userId: 'Vueling Airlines' },
    { name: 'WestJet', userId: 'WestJet' },
    { name: 'Wizzair', userId: 'Wizzair' },
    { name: 'Xiamen Airlines', userId: 'Xiamen Airlines' }
  ];
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedPassengers.length === 0) {
      alert('Please select at least one passenger');
      return;
    }
  
    try {
      setIsLoading(true);
      
      let ticketPdfUrl = '';
      let ticketFilePath = '';
      let originalFileName = '';
      
      if (selectedFile) {
        // Upload file using the upload function
        const uploadResult = await uploadFile(selectedFile);
        
        if (!uploadResult || !uploadResult.url) {
          throw new Error('Upload failed or returned invalid result');
        }
        
        // Use the direct URL from upload response
        ticketPdfUrl = uploadResult.url;
        ticketFilePath = uploadResult.filePath;
        originalFileName = uploadResult.fileName || selectedFile.name;
      }
  
      const db = getFirestore();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Create the ticket object
      const ticketToSave = {
        passengerName: ticketData.passengerName || '',
        origin: ticketData.origin || '',
        destination: ticketData.destination || '',
        departureTime: Timestamp.fromDate(new Date(ticketData.departureTime || Date.now())),
        arrivalTime: Timestamp.fromDate(new Date(ticketData.arrivalTime || Date.now())),
        flightNumber: ticketData.flightNumber || '',
        trainNumber: ticketData.trainNumber || '',
        busNumber: ticketData.busNumber || '',
        pnrNumber: ticketData.pnrNumber || '',
        notes: ticketData.notes || '',
        airlines: ticketData.airlines || '',
        travelMode: ticketData.travelMode || '',
        ticketPdfUrl: ticketPdfUrl || (editingTicket ? editingTicket.ticketPdfUrl || '' : ''),
        ticketFilePath: ticketFilePath || (editingTicket ? editingTicket.ticketFilePath || '' : ''),
        originalFileName: originalFileName || (editingTicket ? editingTicket.originalFileName || '' : ''),
        passengers: selectedPassengers.map(p => ({
          userId: p.userId,
          fullName: p.name
        })),
        visibility: selectedVisibility.map(member => member.userId),
        createdBy: {
          userId: userData.userId,
          fullName: userData.fullName,
          userName: userData.userName,
          profilePictureUrl: userData.profilePictureUrl || ''
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      // Reference to the tickets subcollection
      const ticketsCollectionRef = collection(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'tickets');
      
      if (editingTicket) {
        // Update existing ticket document
        const ticketDocRef = doc(db, collectionName, selectedEvent.id, 'eventDates', selectedDate, 'tickets', editingTicket.id);
        await updateDoc(ticketDocRef, ticketToSave);
      } else {
        // Add new ticket document to subcollection
        await addDoc(ticketsCollectionRef, ticketToSave);
      }
      
      // Update the event data in context to refresh UI
      await updateDate(selectedEvent.id, selectedDate, eventType);
      
      resetForm();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving ticket:', error);
      alert('Failed to save ticket. Please try again.');
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
    }
  };
  
  // Updated to get tour members from assignedUsers subcollection
  const getTourMembers = async () => {
    if (!selectedEvent || !selectedDate) return [];
    
    try {
      let members = [];
      const db = getFirestore();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Fetch members from assignedUsers subcollection
      const assignedUsersRef = collection(db, collectionName, selectedEvent.id, 'assignedUsers');
      const assignedUsersSnapshot = await getDocs(assignedUsersRef);
      
      // Get assigned users for this specific date
      for (const userDoc of assignedUsersSnapshot.docs) {
        const userData = userDoc.data();
        const userId = userDoc.id;
        
        // Check if this user is assigned to the current date
        const isAssigned = userData.assignedDates?.some(date => date.dateDocId === selectedDate);
        
        if (isAssigned) {
          // Fetch user details
          const userRef = doc(db, 'users', userId);
          const userSnapshot = await getDoc(userRef);
          
          if (userSnapshot.exists()) {
            const user = userSnapshot.data();
            members.push({
              name: user.fullName || user.displayName,
              userId: userId,
              profilePictureUrl: user.profilePictureUrl,
              username: user.userName,
              userType: user.userType
            });
          }
        }
      }
      
      console.log('Available members for date:', members);
      return members;
    } catch (error) {
      console.error('Error fetching tour members:', error);
      return [];
    }
  };
  
  // Use useState with an async effect to fetch members data
  const [availableMembers, setAvailableMembers] = useState([]);
  const [isFetchingMembers, setIsFetchingMembers] = useState(false);
  
  // Add this effect to fetch members data when the component mounts or when dependencies change
  useEffect(() => {
    const fetchMembers = async () => {
      if (!selectedEvent || !selectedDate) return;
      
      setIsFetchingMembers(true);
      try {
        const members = await getTourMembers();
        setAvailableMembers(members);
        
        // Now set visibility based on the fetched members
        if (editingTicket && visibilityIds.length > 0) {
          // For editing tickets with existing visibility
          const visibilityMembers = visibilityIds
            .map(userId => members.find(member => member.userId === userId))
            .filter(Boolean); // Remove any undefined values
          setSelectedVisibility(visibilityMembers);
        } else if (userData?.membershipTier !== 'Elite') {
          // Default for non-Elite: visible to all
          setSelectedVisibility(members);
        } else {
          // For Elite members, start with only the manager selected
          const manager = members.find(m => m.userType === 'Manager');
          setSelectedVisibility(manager ? [manager] : []);
        }
      } catch (error) {
        console.error('Error fetching members:', error);
      } finally {
        setIsFetchingMembers(false);
      }
    };
    
    fetchMembers();
  }, [selectedEvent, selectedDate, userData?.membershipTier, editingTicket, visibilityIds, eventType]);

  const renderForm = () => {
    if (!selectedMode) {
      return (
        <div className={styles['travel-mode-options']}>
          <div 
            className={styles['travel-mode-card']}
            onClick={() => handleModeSelect('Flight')}
          >
            <FontAwesomeIcon icon={faPlane} className={styles['travel-mode-icon']} />
            <span className={styles['travel-mode-text']}>Flight</span>
          </div>
          <div 
            className={styles['travel-mode-card']}
            onClick={() => handleModeSelect('Train')}
          >
            <FontAwesomeIcon icon={faTrain} className={styles['travel-mode-icon']} />
            <span className={styles['travel-mode-text']}>Train</span>
          </div>
          <div 
            className={styles['travel-mode-card']}
            onClick={() => handleModeSelect('Bus')}
          >
            <FontAwesomeIcon icon={faBus} className={styles['travel-mode-icon']} />
            <span className={styles['travel-mode-text']}>Bus</span>
          </div>
        </div>
      );
    }

  return (
    <form onSubmit={handleSubmit} className={styles['ticket-form']}>
      <div className={styles['form-section']}>
      <div className={styles['form-group']}>
  <label className={styles['required-field']}>Passengers</label>
  <div className={styles['passenger-dropdown']}>
    <button
      type="button"
      className={styles['dropdown-button']}
      onClick={() => setIsPassengerDropdownOpen(!isPassengerDropdownOpen)}
    >
      <span>{selectedPassengers.length ? `${selectedPassengers.length} passenger(s) selected` : 'Select passengers'}</span>
      <FontAwesomeIcon icon={isPassengerDropdownOpen ? faChevronUp : faChevronDown} />
    </button>
    
    {isPassengerDropdownOpen && (
      <div className={styles['dropdown-content']}>
        {availableMembers.map(member => (
          <div
            key={member.userId}
            className={`${styles['passenger-option']} ${
              selectedPassengers.some(p => p.userId === member.userId) ? styles['selected'] : ''
            }`}
            onClick={() => {
              const isSelected = selectedPassengers.some(p => p.userId === member.userId);
              if (isSelected) {
                setSelectedPassengers(prev => prev.filter(p => p.userId !== member.userId));
              } else {
                setSelectedPassengers(prev => [...prev, member]);
              }
            }}
          >
            <img
              src={member.profilePictureUrl || defaultProfilePhoto}
              alt={member.name}
              className={styles['passenger-avatar']}
            />
            <div className={styles['passenger-info']}>
              <span className={styles['passenger-name']}>{member.name}</span>
              <span className={styles['passenger-username']}>@{member.username}</span>
              <span className={styles['passenger-usertype']}>{member.userType}</span>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>

  {selectedPassengers.length > 0 && (
    <div className={styles['selected-passengers']}>
      {selectedPassengers.map(passenger => (
        <div key={passenger.userId} className={styles['selected-passenger-tag']}>
          <img
            src={passenger.profilePictureUrl || defaultProfilePhoto}
            alt={passenger.name}
          />
          <span>{passenger.name}</span>
          <span 
            className={styles['remove-passenger']}
            onClick={() => setSelectedPassengers(prev => 
              prev.filter(p => p.userId !== passenger.userId)
            )}
          >
            <FontAwesomeIcon icon={faTimes} />
          </span>
        </div>
      ))}
    </div>
  )}
</div>
  
  {selectedMode === 'Flight' && (
  <div className={styles['form-group']}>
    <label className={styles['required-field']}>Airlines</label>
    <select
      required
      value={ticketData.airlines}
      onChange={(e) => setTicketData(prev => ({
        ...prev,
        airlines: e.target.value
      }))}
      className={styles['form-input']}
    >
      <option value="">Select Airline</option>
      {airlines.map(airline => (
        <option key={airline.userId} value={airline.name}>
          {airline.name}
        </option>
      ))}
    </select>
  </div>
)}
  
        <div className={styles['form-group']}>
          <label>Upload Ticket (PDF)</label>
          <div className={styles['file-upload']}>
            <label className={styles['file-upload-label']}>
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => {
                  setSelectedFile(e.target.files[0]);
                }}
              />
              <FontAwesomeIcon icon={faUpload} />
              {selectedFile ? 'Change file' : 'Choose PDF file'}
            </label>
            {selectedFile && (
              <div className={styles['file-name']}>
                Selected: {selectedFile.name}
              </div>
            )}
          </div>
        </div>
  
        {userData?.membershipTier === 'Elite' && (
        <div className={styles['form-group']}>
          <label className={styles['required-field']}>Ticket Visibility</label>
          <div className={styles['visibility-selector']}>
            {isFetchingMembers ? (
              <div className={styles['loading-members']}>
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>Loading members...</span>
              </div>
            ) : (
              availableMembers.map(member => {
                const isManager = member.userType === 'Manager';
                return (
                  <div 
                    key={member.userId} 
                    className={`${styles['visibility-option']} ${isManager ? styles['manager'] : ''}`}
                  >
                    <input
                      type="checkbox"
                      id={member.userId}
                      checked={isManager || selectedVisibility.some(v => v.userId === member.userId)}
                      onChange={(e) => {
                        if (!isManager) {
                          if (e.target.checked) {
                            setSelectedVisibility([...selectedVisibility, member]);
                          } else {
                            setSelectedVisibility(selectedVisibility.filter(v => v.userId !== member.userId));
                          }
                        }
                      }}
                      disabled={isManager}
                    />
                    <label htmlFor={member.userId}>
                      {member.name} ({member.userType})
                    </label>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}      </div>
  
      <div className={styles['form-section']}>
    <div className={styles['form-group']}>
  <label className={styles['required-field']}>From</label>
  <input
    type="text"
    required
    value={ticketData.origin}
    onChange={(e) => setTicketData(prev => ({
      ...prev,
      origin: e.target.value
    }))}
    className={styles['form-input']}
    placeholder="Enter departure city"
  />
</div>
        <div className={styles['form-group']}>
        <label className={styles['required-field']}>To</label>
          <input
            type="text"
            required
            value={ticketData.destination}
            onChange={(e) => setTicketData(prev => ({
              ...prev,
              destination: e.target.value
            }))}
            className={styles['form-input']}
            placeholder="Enter arrival city"
          />
        </div>
      </div>
  
      <div className={styles['form-section']}>
      <div className={styles['form-group']}>
  <label className={styles['required-field']}>Departure Time</label>
  <div className={styles['date-picker-wrapper']}>
    <DatePicker
      selected={ticketData.departureTime ? new Date(ticketData.departureTime) : null}
      onChange={(date) => {
        setTicketData(prev => ({
          ...prev,
          departureTime: date ? date.toISOString() : ''
        }));
      }}
      showTimeSelect
      timeFormat="HH:mm"
      timeIntervals={15}
      dateFormat="dd/MM/yyyy HH:mm"
      placeholderText="Select departure date and time"
      required
      minDate={new Date()}
      className={styles['form-input']}
    />
  </div>
</div>

<div className={styles['form-group']}>
  <label className={styles['required-field']}>Arrival Time</label>
  <div className={styles['date-picker-wrapper']}>
    <DatePicker
      selected={ticketData.arrivalTime ? new Date(ticketData.arrivalTime) : null}
      onChange={(date) => {
        setTicketData(prev => ({
          ...prev,
          arrivalTime: date ? date.toISOString() : ''
        }));
      }}
      showTimeSelect
      timeFormat="HH:mm"
      timeIntervals={15}
      dateFormat="dd/MM/yyyy HH:mm"
      placeholderText="Select arrival date and time"
      required
      minDate={ticketData.departureTime ? new Date(ticketData.departureTime) : new Date()}
      className={styles['form-input']}
    />
  </div>
</div>
        </div>

      
  
      <div className={styles['form-row']}>
          {selectedMode === 'Flight' && (
            <div className={styles['form-group']}>
            <label className={styles['required-field']}>Flight Number</label>
              <input
                type="text"
                value={ticketData.flightNumber}
                onChange={(e) => setTicketData(prev => ({
                  ...prev,
                  flightNumber: e.target.value
                }))}
                className={styles['form-input']}
              />
            </div>
          )}
          {selectedMode === 'Train' && (
            <div className={styles['form-group']}>
             <label className={styles['required-field']}>Train Number</label>
              <input
                type="text"
                value={ticketData.trainNumber}
                onChange={(e) => setTicketData(prev => ({
                  ...prev,
                  trainNumber: e.target.value
                }))}
                className={styles['form-input']}
              />
            </div>
          )}
          {selectedMode === 'Bus' && (
            <div className={styles['form-group']}>
            <label className={styles['required-field']}>Bus Number</label>
              <input
                type="text"
                value={ticketData.busNumber}
                onChange={(e) => setTicketData(prev => ({
                  ...prev,
                  busNumber: e.target.value
                }))}
                className={styles['form-input']}
              />
            </div>
          )}
          <div className={styles['form-group']}>
            <label>PNR Number</label>
            <input
              type="text"
              value={ticketData.pnrNumber}
              onChange={(e) => setTicketData(prev => ({
                ...prev,
                pnrNumber: e.target.value
              }))}
              className={styles['form-input']}
            />
          </div>
        </div>

        <div className={styles['form-row']}>
          <div className={`${styles['form-group']} ${styles['full-width']}`}>
            <label>Notes</label>
            <input
              type="text"
              value={ticketData.notes}
              onChange={(e) => setTicketData(prev => ({
                ...prev,
                notes: e.target.value
              }))}
              className={styles['form-input']}
            />
          </div>
        </div>
  
      <div className={styles['modal-actions']}>
        <button
          type="button"
          className={`${styles['modal-button']} ${styles['secondary']}`}
          onClick={() => {
            if (selectedMode) {
              setSelectedMode(null);
            } else {
              setIsModalOpen(false);
              resetForm();
            }
          }}
        >
          {selectedMode ? 'Back' : 'Cancel'}
        </button>
        {selectedMode && (
            <button type="submit" className={`${styles['modal-button']} ${styles['primary']}`}>
  {editingTicket ? 'Update Ticket' : 'Add Ticket'}
</button>
        )}
      </div>
    </form>
  );
  };

  return (
    <>
      {isModalOpen && (
        <div className={styles['modal-overlay']}>
          <div className={styles['modal-content']}>
            {isLoading && (
              <div className={styles['loading-overlay']}>
                <div className={styles['loading-spinner']} />
                {uploadProgress > 0 && (
                  <div className={styles['progress-container']}>
                    <div 
                      className={styles['progress-bar']} 
                      style={{ width: `${uploadProgress}%` }}
                    />
                    <span className={styles['progress-text']}>{uploadProgress}%</span>
                  </div>
                )}
              </div>
            )}
            <div className={styles['modal-header']}>
              <h2>{selectedMode ? `${editingTicket ? 'Update' : 'Add'} ${selectedMode} Ticket` : 'Select Travel Mode'}</h2>
              <button 
                className={styles['close-button']}
                onClick={() => {
                  setIsModalOpen(false);
                  resetForm();
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            {renderForm()}
          </div>
        </div>
      )}
    </>
  );
};

export default TicketModal;